import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import app from '@/firebase';

// Get Firebase Functions instance
const functions = getFunctions(app);

// Get Firebase Messaging instance
const messagingInstance = getMessaging(app);

// Generate unique device ID for web app
const generateDeviceId = (): string => {
  // Use existing device ID from localStorage or generate new one
  let deviceId = localStorage.getItem('convive_device_id');
  if (!deviceId) {
    deviceId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('convive_device_id', deviceId);
  }
  return deviceId;
};

// Device management functions
export interface DeviceInfo {
  deviceId: string;
  userId: string;
  fcmToken: string;
  deviceType: 'web' | 'ios' | 'android';
  appVersion: string;
  lastSeen: Date;
  isActive: boolean;
  userAgent?: string;
}

// Save device token to Firestore
export const saveDeviceToken = async (userId: string, fcmToken: string): Promise<void> => {
  try {
    const deviceId = generateDeviceId();
    const deviceInfo: DeviceInfo = {
      deviceId,
      userId,
      fcmToken,
      deviceType: 'web',
      appVersion: '1.0.0', // You can get this from package.json
      lastSeen: new Date(),
      isActive: true,
      userAgent: navigator.userAgent
    };

    await setDoc(doc(db, 'devices', deviceId), deviceInfo);
  } catch (error) {
    console.error('❌ Error saving device token:', error);
    throw error;
  }
};

// Get all active devices for a user
export const getUserDevices = async (userId: string): Promise<DeviceInfo[]> => {
  try {
    const devicesQuery = query(
      collection(db, 'devices'),
      where('userId', '==', userId),
      where('isActive', '==', true)
    );
    
    const devicesSnapshot = await getDocs(devicesQuery);
    const devices: DeviceInfo[] = [];
    
    devicesSnapshot.forEach(doc => {
      const data = doc.data();
      devices.push({
        ...data,
        lastSeen: data.lastSeen?.toDate() || new Date()
      } as DeviceInfo);
    });
    

    return devices;
  } catch (error) {
    console.error('❌ Error getting user devices:', error);
    return [];
  }
};

// Deactivate a device (when user logs out or token is invalid)
export const deactivateDevice = async (deviceId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'devices', deviceId), {
      isActive: false,
      lastSeen: new Date()
    });

  } catch (error) {
    console.error('❌ Error deactivating device:', error);
  }
};

// Update device last seen timestamp
export const updateDeviceLastSeen = async (deviceId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'devices', deviceId), {
      lastSeen: new Date()
    });
  } catch (error) {
    console.error('❌ Error updating device last seen:', error);
  }
};

// FCM Notification sending functions using Cloud Functions
export interface FCMNotificationPayload {
  token: string; // FCM token
  notification: {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    sound?: string;
  };
  data: {
    type: string;
    threadId?: string;
    messageId?: string;
    senderId?: string;
    [key: string]: any;
  };
  android?: {
    priority?: 'normal' | 'high';
    notification?: {
      sound?: string;
      priority?: 'high' | 'max';
      channel_id?: string;
    };
  };
  apns?: {
    payload: {
      aps: {
        sound?: string;
        badge?: number;
        content_available?: boolean;
        category?: string;
      };
    };
  };
}

// Send FCM notification to a specific token using Cloud Functions
export const sendFCMNotification = async (
  fcmToken: string, 
  notification: Omit<FCMNotificationPayload, 'token'>
): Promise<boolean> => {
  try {

    
    
    // Call the Cloud Function
    const sendFCMFunction = httpsCallable(functions, 'sendFCMNotification');
    await sendFCMFunction({
      fcmToken,
      notification
    });
    
    
    return true;
  } catch (error) {
    console.error('❌ Error sending FCM notification:', error);
    return false;
  }
};

// Send chat message notification to a specific token using Cloud Functions
export const sendChatMessageNotification = async (
  recipientFCMToken: string,
  senderName: string,
  messageText: string,
  threadId: string,
  messageId: string,
  senderId: string,
  deviceType: 'web' | 'ios' | 'android',
  imageUrl?: string
): Promise<boolean> => {
  try {
    
    
    // Call the Cloud Function
    const sendChatFCMFunction = httpsCallable(functions, 'sendChatMessageNotification');
    await sendChatFCMFunction({
      recipientFCMToken,
      senderName,
      messageText,
      threadId,
      messageId,
      senderId,
      deviceType,
      imageUrl
    });
    
    
    return true;
  } catch (error) {
    console.error('❌ Error sending chat FCM notification:', error);
    return false;
  }
};

// Send FCM notification to a specific user using Cloud Functions
export const sendFCMNotificationToUser = async (
  userId: string, 
  notification: Omit<FCMNotificationPayload, 'token'>
): Promise<{ success: number; failed: number }> => {
  try {
    
    
    // Get all user's active devices
    const devices = await getUserDevices(userId);
    
    if (devices.length === 0) {
      return { success: 0, failed: 0 };
    }
    
    // Send to each device
    let success = 0;
    let failed = 0;
    
    for (const device of devices) {
      try {
        const result = await sendFCMNotification(device.fcmToken, notification);
        if (result) {
          success++;
        } else {
          failed++;
        }
      } catch (error) {
        failed++;
        console.error(`❌ Error sending to device ${device.deviceId}:`, error);
      }
    }
    

    return { success, failed };
    
  } catch (error) {
    console.error('❌ Error sending FCM notification to user:', error);
    return { success: 0, failed: 1 };
  }
};

// Send chat message notification to a specific user using Cloud Functions
export const sendChatMessageNotificationToUser = async (
  userId: string,
  senderName: string,
  messageText: string,
  threadId: string,
  messageId: string,
  senderId: string
): Promise<{ success: number; failed: number }> => {
  try {

    
    // Get all user's active devices
    const devices = await getUserDevices(userId);
    
    if (devices.length === 0) {
      return { success: 0, failed: 0 };
    }
    
    // Send to each device
    let success = 0;
    let failed = 0;
    
            for (const device of devices) {
          try {
            const result = await sendChatMessageNotification(
              device.fcmToken,
              senderName,
              messageText,
              threadId,
              messageId,
              senderId,
              device.deviceType, // Pass device type to cloud function
              "https://myapartment.app/images/logo-new.webp" // Add the imageUrl
            );
            
            if (result) {
              success++;
            } else {
              failed++;
            }
          } catch (error) {
            failed++;
            console.error(`❌ Error sending chat notification to device ${device.deviceId}:`, error);
          }
        }
    

    return { success, failed };
    
  } catch (error) {
    console.error('❌ Error sending chat FCM notification to user:', error);
    return { success: 0, failed: 1 };
  }
};

// Send FCM notification to multiple users
export const sendFCMNotificationToUsers = async (
  fcmTokens: string[],
  notification: Omit<FCMNotificationPayload, 'token'>
): Promise<{ success: number; failed: number }> => {
  let success = 0;
  let failed = 0;
  
  // Send to each token individually (FCM doesn't support multiple recipients in one request)
  for (const token of fcmTokens) {
    const result = await sendFCMNotification(token, notification);
    
    if (result) {
      success++;
    } else {
      failed++;
    }
  }
  

  return { success, failed };
};

// FCM Token management for Web App
export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

export const getFCMToken = async (userId?: string): Promise<string | null> => {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey: process.env.REACT_APP_FCM_VAPID_KEY
    });
    
    if (token && userId) {
      // Save token to device collection
      await saveDeviceToken(userId, token);
      
      return token;
    } else if (token) {

      return token;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

export const deleteFCMToken = async (deviceId?: string): Promise<void> => {
  try {
    if (deviceId) {
      // Deactivate device in Firestore
      await deactivateDevice(deviceId);
    }
    
    // Delete token from Firebase
    await deleteToken(messagingInstance);

  } catch (error) {
    console.error('Error deleting FCM token:', error);
  }
};

export const onMessageReceived = (callback: (payload: any) => void) => {
  try {
    return onMessage(messagingInstance, callback);
  } catch (error) {
    console.error('Error setting up message listener:', error);
    return () => {}; // Return empty cleanup function
  }
};

export const getMessagingInstance = async () => {
  return messagingInstance;
};
